/** @jsxImportSource @emotion/react */

/**
 * Module dependencies.
 */

import { InViewOnce } from '../in-view-once/in-view-once';
import { JSX, ReactNode } from 'react';
import { StateDelay } from 'components/providers/animation-delay-provider/animation-delay-provider';
import { TitleComponent } from './title.style';
import { TypographyProps } from '@mui/material';
import { css } from '@emotion/react';

/**
 * `Props` type.
 */

type Props = TypographyProps & {
  center?: boolean;
  children?: ReactNode;
  className?: string;
};

/**
 * `TitleAnimationProps` type.
 */

type TitleAnimationProps = Props & {
  state: StateDelay;
};

/**
 * `TitleAnimation` component.
 */

function TitleAnimation(props: TitleAnimationProps): JSX.Element | null {
  const { center, children, className, state, ...rest } = props;

  if (typeof children !== 'string') {
    return null;
  }

  const words = children.trim().split(' ');
  const baseDelay = state.delay;
  const delayStep = 100;

  return (
    <TitleComponent center={center} className={className} {...rest}>
      {words.map((word, i) => (
        <span
          css={css`
            display: inline-block;
            opacity: ${state.hasEntered ? 1 : 0};
            transform: translateY(${state.hasEntered ? '0px' : '20px'});
            transition:
              opacity 0.5s ease-out,
              transform 0.5s ease-out;
            transition-delay: ${baseDelay + i * delayStep}ms;
            will-change: opacity, transform;
            margin-right: 8px;
          `}
          key={i}
        >
          {word}
        </span>
      ))}
    </TitleComponent>
  );
}

/**
 * Export `Title` component.
 */

export function Title(props: Props): JSX.Element {
  const { center, children, className, ...rest } = props;

  return (
    <InViewOnce>
      {(state) => (
        <TitleAnimation
          center={center}
          className={className}
          component={'h2'}
          state={state}
          variant={'h3'}
          {...rest}
        >
          {children}
        </TitleAnimation>
      )}
    </InViewOnce>
  );
}
